import { ChangeEvent, useCallback, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  TextField,
  Typography
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { uploadGuideDocument } from '../../services/storageService'
import type Database from '../../types/Database'

const ALLOWED_EXTENSIONS = '.jpg,.jpeg,.png,.pdf'

type UploadSlotState = 'idle' | 'uploading' | 'complete' | 'error'

export interface GuidedDiveRequirementsProps {
  userId: string
  provider: string
  disabled?: boolean
  onDocsReady: (docs: Database.GuidedDiveSubmissionDocs | null) => void
}

interface SlotState {
  state: UploadSlotState
  progress: number
  fileName: string | null
  error: string | null
  url: string | null
}

const initialSlot: SlotState = {
  state: 'idle',
  progress: 0,
  fileName: null,
  error: null,
  url: null
}

const LOCAL_REGULATIONS_TEXT =
  'I confirm that I hold all local maritime, commercial boating/skipper licenses, ' +
  'workplace first aid certifications, and business permits required to commercially ' +
  'guide paid dives in my jurisdiction.'

const GuidedDiveRequirements = ({
  userId,
  provider,
  disabled = false,
  onDocsReady
}: GuidedDiveRequirementsProps) => {
  const [professionalStatus, setProfessionalStatus] =
    useState<SlotState>(initialSlot)
  const [insurance, setInsurance] = useState<SlotState>(initialSlot)
  const [professionalStatusExpiry, setProfessionalStatusExpiry] = useState('')
  const [insuranceExpiry, setInsuranceExpiry] = useState('')
  const [affiliatedCenter, setAffiliatedCenter] = useState('')
  const [localRegAcknowledged, setLocalRegAcknowledged] = useState(false)

  const profInputRef = useRef<HTMLInputElement>(null)
  const insInputRef = useRef<HTMLInputElement>(null)

  // Only SSI and RAID require an affiliated center
  const requiresAffiliation = provider === 'ssi' || provider === 'raid'

  // Emit docs to parent whenever all fields are ready (or null if incomplete)
  const emitDocs = useCallback(
    (overrides?: Partial<{
      profUrl: string | null
      profExpiry: string
      insUrl: string | null
      insExpiry: string
      center: string
      localReg: boolean
    }>) => {
      const profUrl = overrides?.profUrl ?? professionalStatus.url
      const profExp = overrides?.profExpiry ?? professionalStatusExpiry
      const insUrl = overrides?.insUrl ?? insurance.url
      const insExp = overrides?.insExpiry ?? insuranceExpiry
      const center = overrides?.center ?? affiliatedCenter
      const localReg = overrides?.localReg ?? localRegAcknowledged

      const centerValid = requiresAffiliation ? center.trim().length > 0 : true

      if (
        profUrl &&
        profExp &&
        insUrl &&
        insExp &&
        centerValid &&
        localReg
      ) {
        onDocsReady({
          professionalStatusUrl: profUrl,
          professionalStatusExpiry: new Date(profExp),
          insuranceUrl: insUrl,
          insuranceExpiry: new Date(insExp),
          affiliatedCenter: requiresAffiliation ? center.trim() : null,
          localRegulationsAcknowledged: true
        })
      } else {
        onDocsReady(null)
      }
    },
    [
      professionalStatus.url,
      professionalStatusExpiry,
      insurance.url,
      insuranceExpiry,
      affiliatedCenter,
      localRegAcknowledged,
      requiresAffiliation,
      onDocsReady
    ]
  )

  const handleUpload = useCallback(
    async (
      file: File,
      category: Database.GuidedDiveDocCategory,
      setState: React.Dispatch<React.SetStateAction<SlotState>>,
      urlKey: 'profUrl' | 'insUrl'
    ) => {
      setState(s => ({
        ...s,
        state: 'uploading',
        progress: 0,
        error: null,
        fileName: file.name
      }))

      try {
        const url = await uploadGuideDocument(
          file,
          userId,
          category,
          percent => {
            setState(s => ({ ...s, progress: Math.round(percent) }))
          }
        )
        setState(s => ({ ...s, state: 'complete', progress: 100, url }))
        emitDocs({ [urlKey]: url })
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setState(s => ({ ...s, state: 'error', error: msg }))
      }
    },
    [userId, emitDocs]
  )

  const handleFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    category: Database.GuidedDiveDocCategory,
    setState: React.Dispatch<React.SetStateAction<SlotState>>,
    urlKey: 'profUrl' | 'insUrl'
  ) => {
    const file = e.target.files?.[0]
    if (!file) return
    handleUpload(file, category, setState, urlKey)
  }

  const renderUploadSlot = (
    label: string,
    slot: SlotState,
    inputRef: React.RefObject<HTMLInputElement | null>,
    category: Database.GuidedDiveDocCategory,
    setState: React.Dispatch<React.SetStateAction<SlotState>>,
    urlKey: 'profUrl' | 'insUrl',
    expiryValue: string,
    onExpiryChange: (val: string) => void,
    expiryLabel: string
  ) => (
    <Box sx={{ my: 2, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
      <Typography variant='subtitle2' gutterBottom>
        {label}
      </Typography>

      <input
        ref={inputRef}
        type='file'
        accept={ALLOWED_EXTENSIONS}
        onChange={e => handleFileChange(e, category, setState, urlKey)}
        disabled={disabled || slot.state === 'uploading'}
        hidden
        aria-label={`Upload ${label}`}
      />

      {slot.state === 'idle' && (
        <Button
          variant='outlined'
          size='small'
          startIcon={<CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          Upload Evidence
        </Button>
      )}

      {slot.state === 'uploading' && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress variant='determinate' value={slot.progress} />
            </Box>
            <Typography variant='body2' color='text.secondary'>
              {slot.progress}%
            </Typography>
          </Box>
          {slot.fileName && (
            <Typography variant='caption' color='text.secondary'>
              Uploading: {slot.fileName}
            </Typography>
          )}
        </Box>
      )}

      {slot.state === 'complete' && (
        <Alert severity='success' sx={{ mt: 1 }}>
          {slot.fileName} uploaded
        </Alert>
      )}

      {slot.state === 'error' && (
        <Box sx={{ mt: 1 }}>
          <Alert severity='error' sx={{ mb: 1 }}>
            {slot.error}
          </Alert>
          <Button
            variant='outlined'
            size='small'
            onClick={() => {
              setState(initialSlot)
              inputRef.current?.click()
            }}
          >
            Retry
          </Button>
        </Box>
      )}

      <TextField
        fullWidth
        label={expiryLabel}
        type='date'
        size='small'
        margin='dense'
        slotProps={{ inputLabel: { shrink: true } }}
        value={expiryValue}
        onChange={e => {
          onExpiryChange(e.target.value)
          emitDocs(
            urlKey === 'profUrl'
              ? { profExpiry: e.target.value }
              : { insExpiry: e.target.value }
          )
        }}
        disabled={disabled}
        helperText='Must include the expiry date visible on your document'
      />
    </Box>
  )

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant='subtitle1' fontWeight={600} gutterBottom>
        Professional Requirements for Guided Dives
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        To offer guided dives you must provide evidence of the following. Your
        eligibility will expire when either your professional status or insurance
        expires.
      </Typography>

      {/* 1. Professional Status */}
      {renderUploadSlot(
        'Active Professional Status (annual renewal proof)',
        professionalStatus,
        profInputRef,
        'professional-status',
        setProfessionalStatus,
        'profUrl',
        professionalStatusExpiry,
        setProfessionalStatusExpiry,
        'Professional Status Expiry Date'
      )}

      {/* 2. Insurance */}
      {renderUploadSlot(
        'Professional Liability Insurance Certificate',
        insurance,
        insInputRef,
        'insurance',
        setInsurance,
        'insUrl',
        insuranceExpiry,
        setInsuranceExpiry,
        'Insurance Expiry Date'
      )}

      {/* 3. Affiliated Center (SSI/RAID only) */}
      {requiresAffiliation && (
        <TextField
          fullWidth
          label='Affiliated Dive/Training Center'
          margin='normal'
          value={affiliatedCenter}
          onChange={e => {
            setAffiliatedCenter(e.target.value)
            emitDocs({ center: e.target.value })
          }}
          disabled={disabled}
          helperText='Name of the SSI/RAID Training Center or Dive Center you are associated with'
          slotProps={{
            htmlInput: { maxLength: 200 }
          }}
        />
      )}

      {/* 4. Local Regulations Acknowledgement */}
      <FormControlLabel
        control={
          <Checkbox
            checked={localRegAcknowledged}
            onChange={e => {
              setLocalRegAcknowledged(e.target.checked)
              emitDocs({ localReg: e.target.checked })
            }}
            disabled={disabled}
          />
        }
        label={
          <Typography variant='body2'>{LOCAL_REGULATIONS_TEXT}</Typography>
        }
        sx={{ mt: 1, alignItems: 'flex-start' }}
      />
    </Box>
  )
}

export default GuidedDiveRequirements
